import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { geneticsApi } from '../../api/genetics';
import { GENDER_ICON_FEMALE, GENDER_ICON_MALE, MULTIPLY_SIGN } from '../../utils/genderDisplay';

export default function BreedingOutcomesReport() {
  const [data, setData] = useState<Awaited<ReturnType<typeof geneticsApi.getBreedingOutcomesReport>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await geneticsApi.getBreedingOutcomesReport({ limit: 100 });
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load breeding outcomes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center text-sm text-slate-600">
        Loading breeding genetics outcomes…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center">
        <p className="text-red-600 text-sm">{error}</p>
        <button type="button" onClick={load} className="mt-2 text-sm text-indigo-700 underline">
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={async () => {
            try {
              const result = await geneticsApi.backfillHatchSnapshots();
              await load();
              alert(`Backfill: ${result.snapshotted} new snapshots, ${result.skipped} already recorded.`);
            } catch (err: unknown) {
              alert(err instanceof Error ? err.message : 'Backfill failed');
            }
          }}
          className="text-sm text-indigo-700 hover:text-indigo-900 underline"
        >
          Backfill missing hatch snapshots
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="bg-white shadow rounded-lg p-4">
          <p className="text-sm text-slate-500">Hatch snapshots</p>
          <p className="text-2xl font-semibold text-slate-900">{data.totalHatchSnapshots}</p>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <p className="text-sm text-slate-500">Banded & compared</p>
          <p className="text-2xl font-semibold text-slate-900">{data.bandedWithOutcome}</p>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <p className="text-sm text-slate-500">Matched prediction</p>
          <p className="text-2xl font-semibold text-green-700">{data.matchedCount}</p>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <p className="text-sm text-slate-500">Match rate</p>
          <p className="text-2xl font-semibold text-slate-900">
            {data.bandedWithOutcome ? `${(data.matchRate * 100).toFixed(0)}%` : '—'}
          </p>
        </div>
      </div>

      {data.records.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-slate-600">
          No hatch prediction snapshots yet. Mark eggs as hatched to record expectations at hatch time.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Parents</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Top prediction</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Actual</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Result</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.records.map((record) => {
                const hasOutcome = record.matched !== null && record.matched !== undefined;
                return (
                  <tr key={record.id}>
                    <td className="px-4 py-3 text-slate-700">
                      {record.maleBandId || GENDER_ICON_MALE} {MULTIPLY_SIGN} {record.femaleBandId || GENDER_ICON_FEMALE}
                    </td>
                    <td className="px-4 py-3 text-slate-800">
                      {record.topPrediction || '—'}
                      {record.topPredictionProbability != null && (
                        <span className="text-slate-500 text-xs ml-1">
                          ({(record.topPredictionProbability * 100).toFixed(0)}%)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-800">
                      {hasOutcome ? record.actualPhenotype || record.actualMutationName || '—' : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {hasOutcome ? (
                        <span title={record.comparisonMessage}>
                          {record.matched ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-600 inline" />
                          ) : (
                            <XCircleIcon className="h-5 w-5 text-amber-600 inline" />
                          )}
                        </span>
                      ) : (
                        <span title="Awaiting banding">
                          <ClockIcon className="h-5 w-5 text-slate-400 inline" />
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {format(new Date(record.hatchedAt), 'MMM d, yyyy')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
